import React, { useEffect } from 'react';
import useRouteBasedVisibility from '../utils/useRouteBasedVisibility';
import { MODULE_SWITCHER_STATE, MODULE_UPDATED } from 'mini-host-bridge/lib/nativeMessageConstants';
import { observer } from 'mobx-react-lite';
import { overrideModuleSwitcherHeight } from 'mini-mail-list-view/lib/utils/mailList/moduleSwitcherHeight';
import { registerListenerForModuleClicked } from '../utils/registerListenerForModuleClicked';
import { sendScenarioDataToHost } from 'mini-host-bridge/lib/utils/sendScenarioDataToHost';
import type Module from 'mini-common-schema/lib/schema/Module';

interface NativeModuleSwitcherProps {
    currentModule: Module;
    onActiveModuleClickAction?: () => void;
}

const NativeModuleSwitcher: React.FC<NativeModuleSwitcherProps> = props => {
    /**
     * Registering the callback here ensures the Native Module Switcher works
     * similarly to Mini's Module Switcher by being immediately available as
     * soon as ModuleSwitcher is rendered by the specific module.
     *
     * Additionally, since Mini re-renders the Module Switcher when
     * `onActiveModuleClickAction` changes, we reset the callback here to ensure
     * the same behavior.
     */
    useEffect(() => {
        registerListenerForModuleClicked(props.onActiveModuleClickAction);
    }, [props.onActiveModuleClickAction]);

    /**
     * Notify Lite that the module switch has completed by passing the
     * MODULE_UPDATED message. Lite uses this to update the selected module.
     */
    useEffect(() => {
        sendScenarioDataToHost({
            type: MODULE_UPDATED,
            data: { module: props.currentModule },
        });
    }, [props.currentModule]);

    const isVisible = useRouteBasedVisibility();
    if (isVisible) {
        return <NativeModuleSwitcherBase />;
    } else {
        return null;
    }
};
export default observer(NativeModuleSwitcher);

const NativeModuleSwitcherBase: React.FC = () => {
    useEffect(() => {
        overrideModuleSwitcherHeight(0);
        sendScenarioDataToHost({
            type: MODULE_SWITCHER_STATE,
            data: { mounted: true },
        });
        return () => {
            overrideModuleSwitcherHeight(56);
            sendScenarioDataToHost({
                type: MODULE_SWITCHER_STATE,
                data: { mounted: false },
            });
        };
    }, []);
    return null;
};
